<template>
  <div>
    <form-component>
      <input-select
        id="appointment-calendar-picker"
        v-model="appointment_calendar_id"
        name="appointment-calendar-picker"
        label=""
        :options="options" />
    </form-component>
    <template v-if="currentCalendarSelectedAndIsReadonly">
      <p class="error-card">
        Your current active calendar is considered readonly. 
        BetterMynd cannot add or remove events from this calendar.
        If you do not change your appointment calendar, your availability 
        may be affected.
      </p>
    </template>
    <template v-else-if="currentCalendarSelectedAndIsDeleted">
      <p class="error-card">
        Your current active calendar is considered deleted. 
        BetterMynd cannot add or remove events from this calendar.
        If you do not change your appointment calendar, your availability 
        may be affected.
      </p>
    </template>
    <template v-else-if="selectedCalendarIsReadonly">
      <p class="warn-card">
        The selected calendar is considered readonly and cannot be chosen. 
        BetterMynd would be unable to add or remove events from the calendar.
        Please select another appointment calendar.
      </p>
    </template>
    <template v-else-if="selectedCalendarIsDeleted">
      <p class="warn-card">
        The selected calendar is considered deleted and cannot be chosen. 
        BetterMynd would be unable to add or remove events from the calendar.
        Please select another appointment calendar.
      </p>
    </template>
    <div class="button-field" v-else-if="!currentCalendarSelected" >
      <button class="btn btn-primary" 
        @click="changeAppointmentCalendar">            
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        /> 
        <span v-else>Change Appointment Calendar</span>
      </button>
    </div>
  </div>
</template>

<script>
import InputSelect from '@/components/inputs/InputSelect.vue'
import FormComponent from '@/components/FormComponent.vue'
import CounselorClient from '@/api/CounselorClient'

export default {
  components: {
    InputSelect,
    FormComponent,
  },
  props: {
    counselorId: {
      type: Number,
      required: true,
    }
  },
  data() {
    return {
      saving: false,
      counselorClient: new CounselorClient(this.$http),
      current_appointment_calendar: "1",
      appointment_calendar_id: "1",
      calendars: [],
    }
  },
  computed:{
    options() {
      return this.calendars.map(x => ({
        id: x.id.toString(),
        display_label: this.calendarToDisplayLabel(x),
      }))
    },
    currentCalendarSelected(){
      return this.appointment_calendar_id === this.current_appointment_calendar
    },

    selectedCalendarIsReadonly(){
      const appointmentCalendar = this.calendars.find(
        x => x.id.toString() === this.appointment_calendar_id);
      return appointmentCalendar && appointmentCalendar.isReadonly
    },
    currentCalendarSelectedAndIsReadonly(){
      return this.currentCalendarSelected
        && this.selectedCalendarIsReadonly;
    },

    selectedCalendarIsDeleted(){
      const appointmentCalendar = this.calendars.find(
        x => x.id.toString() === this.appointment_calendar_id);
      return appointmentCalendar && appointmentCalendar.isDeleted
    },
    currentCalendarSelectedAndIsDeleted(){
      return this.currentCalendarSelected
        && this.selectedCalendarIsDeleted;
    },
  },
  async mounted() {
    const response = await this.counselorClient.getCalendars(this.counselorId);
    this.calendars = response.calendars;
    const appointmentCalendar = this.calendars.find(x => x.isAppointmentCalendar);
    if(appointmentCalendar) {
      this.current_appointment_calendar = appointmentCalendar.id.toString();
      this.appointment_calendar_id = appointmentCalendar.id.toString();
    }
  },
  methods:{
    calendarToDisplayLabel(calendar) {
      if(calendar.accountType == 'application_calendar')
        return 'BetterMynd - Default Calendar';
        
      let suffix = '';
      if(calendar.isDeleted)
        suffix = '(Deleted)'
      else if(calendar.isReadonly)
        suffix = '(Readonly)'

      if(calendar.profileName === calendar.calendarName)
        return `${calendar.profileName} - Default Calendar ${suffix}`

      return `${calendar.profileName} - ${calendar.calendarName} ${suffix}`
    },
    async changeAppointmentCalendar() {
      try {
        this.saving = true;
        
        await this.counselorClient.setAppointmentCalendar(
          this.counselorId,
          Number(this.appointment_calendar_id));

        this.current_appointment_calendar = this.appointment_calendar_id;

        this.$store.commit('setGlobalAlert', {
          msg: `Your appointment calendar has been changed.`, 
        });
      } catch(ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed setting appointment calendar. Please try again. If the issue persists, please contact support.', 
          error: true,
        });
      } finally {
        this.saving = false;
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.warn-card {
  @include border-radius($border-radius);
  @include pop-yellow-gradient;
  color: $black;
  padding: 15px;
}
.error-card {
  @include border-radius($border-radius);
  @include pop-red-gradient;
  padding: 15px;
}
</style>